import { Injectable } from '@angular/core';
import type { ReportDescriptorInterface } from '@zaoblako/shared/common/dtos/report/report-descriptor.dto';
import type { BudgetRequestFindManyQueryInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/page/budget-request-find-many-query.dto';
import type { BudgetRequestPageInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/page/budget-request-page.dto';
import type { CardDescriptorInterface } from '@zaoblako/shared/common/dtos/card/card-descriptor.dto';
import type { EntityCardHistoryQueryInterface } from '@zaoblako/shared/common/dtos/history/entity-card-history-query.dto';
import type { PaginationRequiredQueryInterface } from '@zaoblako/shared/common/dtos/pagination-required-query.dto';
import type { CommentaryCreateInterface } from '@zaoblako/shared/modules/messenger/dtos/commentary/commentary-create.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { BudgetRequestFactAnalyticsItemInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request-fact-analytics-item.dto';
import type { BudgetRequestFactAnalyticsQueryInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request-fact-analytics-query.dto';
import type { BudgetRequestSwitchStatusInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request-switch-status.dto';
import type { BudgetRequestFindInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request-find.dto';
import type { BudgetRequestFilesAttachInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/file/budget-request-files-attach.dto';
import type { BudgetRequestFilesPageInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/file/budget-request-files.page.dto';
import type { HistoryPageInterface } from '@zaoblako/shared/common/dtos/history/history-page.dto';
import type { BudgetRequestItemBulkFormInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/item/budget-request-item-bulk-form.dto';
import type { BudgetRequestItemFindInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/item/budget-request-item-find.dto';
import type { BudgetRequestProjectFormInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/project/budget-request-project-form.dto';
import type { BudgetRequestProjectQueryInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/project/budget-request-project.query.dto';
import type { BudgetRequestItemAutoFillFormInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/item/budget-request-item-auto-fill-form.dto';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BudgetRequestService extends BaseService {
  protected baseUrl = '/finance/plan-fact/budget-request';

  public getReportDescriptor(): Observable<ReportDescriptorInterface> {
    return this.http.get<ReportDescriptorInterface>(this.baseUrl);
  }

  public getCardDescriptor(): Observable<CardDescriptorInterface> {
    return this.http.get<CardDescriptorInterface>(`${this.baseUrl}/card-descriptor`);
  }

  public findMany(params: BudgetRequestFindManyQueryInterface): Observable<BudgetRequestPageInterface> {
    return this.http.get<BudgetRequestPageInterface>(`${this.baseUrl}/items`, {
      params: params
        ? this.objectToHttpParams(params)
        : {}
    });
  }

  public restoreOne(id: number): Observable<BudgetRequestFindInterface> {
    return this.http.post<BudgetRequestFindInterface>(`${this.baseUrl}/${id}/restore`, {});
  }

  public getHistory(id: number, options: EntityCardHistoryQueryInterface): Observable<HistoryPageInterface> {
    return this.http.get<HistoryPageInterface>(`${this.baseUrl}/${id}/history`, {
      params: this.objectToHttpParams(options)
    });
  }

  public getFiles(id: number, options: PaginationRequiredQueryInterface): Observable<BudgetRequestFilesPageInterface> {
    return this.http.get<BudgetRequestFilesPageInterface>(`${this.baseUrl}/${id}/file`, {
      params: this.objectToHttpParams(options)
    });
  }

  public attachFiles(id: number, body: BudgetRequestFilesAttachInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/file`, body);
  }

  public detachOneFile(requestId: number, fileId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${requestId}/file/${fileId}`);
  }

  public getProjects(id: number, options?: BudgetRequestProjectQueryInterface): Observable<ProjectFindInterface[]> {
    return this.http.get<ProjectFindInterface[]>(`${this.baseUrl}/${id}/project`, {
      params: options ? this.objectToHttpParams(options) : {}
    });
  }

  public updateProjects(id: number, body: BudgetRequestProjectFormInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/project`, body);
  }

  public approveOne(requestId: number, userId: number): Observable<BudgetRequestFindInterface> {
    return this.http.post<BudgetRequestFindInterface>(`${this.baseUrl}/${requestId}/approve/${userId}`, null);
  }

  public refuseOne(
    requestId: number,
    userId: number,
    body: CommentaryCreateInterface
  ): Observable<BudgetRequestFindInterface> {
    return this.http.post<BudgetRequestFindInterface>(`${this.baseUrl}/${requestId}/refuse/${userId}`, body);
  }

  public switchStatus(id: number, body: BudgetRequestSwitchStatusInterface): Observable<BudgetRequestFindInterface> {
    return this.http.post<BudgetRequestFindInterface>(`${this.baseUrl}/${id}/switch-status`, body);
  }

  public getMembers(taskId: number): Observable<UserFindInterface[]> {
    return this.http.get<UserFindInterface[]>(`${this.baseUrl}/${taskId}/participants`);
  }

  public getFactAnalyticsBySettings(
    params: BudgetRequestFactAnalyticsQueryInterface
  ): Observable<BudgetRequestFactAnalyticsItemInterface[]> {
    return this.http.get<BudgetRequestFactAnalyticsItemInterface[]>(`${this.baseUrl}/fact-analytics`, {
      params: this.objectToHttpParams(params)
    });
  }

  public updateItemsForOne(
    id: number,
    body: BudgetRequestItemBulkFormInterface
  ): Observable<BudgetRequestItemFindInterface[]> {
    return this.http.post<BudgetRequestItemFindInterface[]>(`${this.baseUrl}/${id}/items`, body);
  }

  public getItemsFlatForOne(id: number): Observable<BudgetRequestItemFindInterface[]> {
    return this.http.get<BudgetRequestItemFindInterface[]>(`${this.baseUrl}/${id}/items/flat`);
  }

  public getItemsTreeForOne(id: number): Observable<BudgetRequestItemFindInterface[]> {
    return this.http.get<BudgetRequestItemFindInterface[]>(`${this.baseUrl}/${id}/items/tree`);
  }

  public autoFillPlan(id: number, body?: BudgetRequestItemAutoFillFormInterface): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/items/auto-fill`, body || {});
  }

  public autoFillTransferExpense(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/items/auto-fill-transfer-expense`, null);
  }
}
